import React, { useState, useContext } from "react";
import {
  Dialog,
  Grid,
  IconButton,
  Typography,
  withStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import { httpPost } from "../../../../API/Http";
import { LoadingIndicator, Upload } from "../../../../Component";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import styles from "./styles";
import UploadForm from "./uploadForm";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../Component/CustomButton";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const ALLOWED_FILE_TYPES = [
  ".pdf",
  ".docx",
  ".xlsx",
  ".pptx",
  ".eml",
  ".msg",
  "image/*",
];
const ONE_MB = 1024 * 1024;

const UploadFilesDialog = (props) => {
  const {
    classes,
    entityId,
    category,
    urn,
    isDialogOpen,
    documentTypes = [],
    onSubmit,
    onClose,
  } = props;

  const [isLoading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const { showMessage, showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const addUploadedFiles = (files) => {
    setLoading(true);
    for (let file of files) {
      setUploadedFiles((files) => [...files, { file }]);
    }
    setLoading(false);
  };

  const setFileData = (index, data) => {
    uploadedFiles[index] = { ...uploadedFiles[index], ...data };
    setUploadedFiles([...uploadedFiles]);
  };

  const validateForm = () => {
    const errors = {};
    let hasError = false;
    for (let i = 0; i < uploadedFiles.length; i++) {
      let fileSize = uploadedFiles[i].file.size / ONE_MB;
      errors[i] = {};
      if (fileSize > 10) {
        errors[i]["size"] = t("messages.record.documents.fileSizeError");
        hasError = true;
      }
      if (!uploadedFiles[i].updatedfileName) {
        errors[i]["filename"] = t("messages.record.documents.fileNameError");
        hasError = true;
      } else if (uploadedFiles[i].updatedfileName.length > 100) {
        errors[i]["filename"] = t(
          "messages.record.documents.fileNameLongError",
        );
        hasError = true;
      }
      if (!uploadedFiles[i].documentType) {
        errors[i]["documentType"] = t(
          "messages.record.documents.documentTypeError",
        );
        hasError = true;
      }
      if (
        uploadedFiles[i].hasExpiry &&
        (!uploadedFiles[i].expiryDate || uploadedFiles[i].expiryDate === "")
      ) {
        errors[i]["expiryDate"] = t(
          "messages.record.documents.expiryDateError",
        );
        hasError = true;
      }
      setErrors(errors);
    }
    return hasError;
  };

  const handleSubmit = async () => {
    const hasError = validateForm();
    if (!hasError) {
      try {
        setLoading(true);
        const formData = new FormData();
        const files = uploadedFiles.map((f) => f.file);
        const fileDetails = uploadedFiles.map((f) => {
          const {
            updatedfileName: filename,
            documentType,
            hasExpiry,
            expiryDate,
          } = f;
          return { filename, documentType, hasExpiry, expiryDate };
        });
        formData.append("fileDetails", JSON.stringify(fileDetails));
        formData.append("entityId", entityId);
        formData.append("category", category);
        formData.append("urn", urn);
        for (let i = 0; i < files.length; i++) {
          formData.append(`data`, files[i]);
        }
        await httpPost("/entity/document", formData);
        setUploadedFiles([]);
        if (onSubmit) {
          await onSubmit();
        }
        showMessage({
          message: t("messages.record.documents.documentsUpload"),
        });
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoading(false);
    }
  };

  const handleRemove = async (index) => {
    const newFiles = uploadedFiles.filter((f, idx) => idx !== index);
    setUploadedFiles([...newFiles]);
  };

  const handleClose = () => {
    setUploadedFiles([]);
    onClose();
  };

  return (
    <Dialog
      disableBackdropClick
      classes={{ paper: classes.dialogPaper }}
      onClose={handleClose}
      aria-labelledby="add-document-modal"
      open={isDialogOpen}
      fullWidth={true}
      fullScreen={true}
    >
      <DialogTitle id="add-document-modal" onClose={handleClose}>
        <Typography className={classes.panelTitle}>
          {t("labels.record.documents.addDocument")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers style={{ overflow: "auto" }}>
        {isLoading ? (
          renderLoadingIndicator()
        ) : (
          <Grid container justify="center">
            <Grid item xs={12}>
              <Upload
                acceptFormats={ALLOWED_FILE_TYPES}
                onFileUpload={addUploadedFiles}
              />
            </Grid>
            <Grid item xs={12}>
              {uploadedFiles.map((uf, index) => {
                return (
                  <UploadForm
                    classes={classes}
                    file={uf.file}
                    fileIndex={index}
                    key={index}
                    handleChange={setFileData}
                    documentTypes={documentTypes}
                    handleRemove={handleRemove}
                    hasDocType={uf.isPredictionDone}
                    selectedType={uf.documentType}
                    hasExpiryDate={uf.hasExpiry}
                    expirationDate={uf.expiryDate}
                    errors={errors[index]}
                  />
                );
              })}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <CustomButton 
          variant="secondary" 
          isDisabled={isLoading}
          onClick={handleClose}
        >
          {t("labels.common.close")}
        </CustomButton>
        <CustomButton 
          variant="primary" 
          isDisabled={isLoading || uploadedFiles.length === 0}
          onClick={handleSubmit}
        >
          {t("labels.common.proceed")}
        </CustomButton>

      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles, { withTheme: true })(UploadFilesDialog);
